"use client";

import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useRedeemableQuery } from "@/hooks/useRedeemableQuery";
import { useRedeemModalStore } from "@/stores/useRedeemModalStore";
import { formatCurrency } from "@/lib/format";
import { useT } from "@/hooks/useT";

interface RedeemableData {
  positions: Array<{
    conditionId: string;
    marketImage?: string | null;
    [key: string]: unknown;
  }>;
  totalRedeemable: number;
  count: number;
}

export default function RedeemablePositionsCard() {
  const t = useT();
  const [data, setData] = useState<RedeemableData | null>(null);
  const [loading, setLoading] = useState(true);
  const { open: openRedeemModal } = useRedeemModalStore();

  const { data: redeemableData, isLoading: redeemableLoading } = useRedeemableQuery();

  useEffect(() => {
    if (redeemableData) {
      setData(redeemableData as any);
      setLoading(false);
    } else if (!redeemableLoading) {
      setLoading(false);
    }
  }, [redeemableData, redeemableLoading]);

  if (loading || !data || data.count === 0) {
    return null;
  }

  const firstImage = data.positions[0]?.marketImage;

  return (
    <div className="rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A]">
      {/* Mobile Layout */}
      <div className="p-4 sm:hidden">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-[#25252B]">
            {firstImage ? (
              <img src={firstImage} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-satoshi font-semibold text-gray-900 dark:text-white">
              {data.count === 1 ? t("portfolio.redeem.wonOne", { n: data.count }) : t("portfolio.redeem.wonMany", { n: data.count })}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("portfolio.redeem.subtitle")}
            </p>
          </div>
          <p className="text-lg font-inter-tight font-bold text-emerald-400 flex-shrink-0">
            {formatCurrency(data.totalRedeemable)}
          </p>
        </div>
        <button
          onClick={openRedeemModal}
          className="w-full py-2.5 rounded-[5px] bg-white dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white font-inter-tight font-bold text-sm hover:bg-gray-50 dark:hover:bg-[#25252B] transition-colors active:scale-[0.98]"
        >
          {t("portfolio.redeem.claimNow")}
        </button>
      </div>

      {/* Desktop Layout */}
      <div className="p-4 hidden sm:flex items-center gap-4">
        <div className="w-11 h-11 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-[#25252B]">
          {firstImage ? (
            <img src={firstImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-satoshi font-semibold text-gray-900 dark:text-white">
            {data.count === 1 ? t("portfolio.redeem.wonOne", { n: data.count }) : t("portfolio.redeem.wonMany", { n: data.count })}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t("portfolio.redeem.subtitleNow")}
          </p>
        </div>

        <div className="text-right mr-2">
          <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wide font-medium">{t("portfolio.redeem.total")}</p>
          <p className="text-xl font-inter-tight font-bold text-emerald-400">
            {formatCurrency(data.totalRedeemable)}
          </p>
        </div>

        <button
          onClick={openRedeemModal}
          className="px-5 py-2.5 rounded-[5px] bg-white text-gray-900 font-inter-tight font-bold text-sm hover:bg-gray-100 transition-colors active:scale-[0.98]"
        >
          {t("portfolio.redeem.claim")}
        </button>
      </div>
    </div>
  );
}
